import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { RequireOrganization } from "@/features/onboarding/RequireOrganization";
import { RouteFallback } from "@/components/RouteFallback";
import { RouteLoadingOverlay } from "@/components/RouteLoadingOverlay";
import { lazyPage } from "@/lib/routeLoading";
import { MarketingLayout } from "@/features/marketing/MarketingLayout";
import { LandingPage } from "@/pages/marketing/LandingPage";
import { PricingPage } from "@/pages/marketing/PricingPage";
import { NotFoundPage } from "@/pages/marketing/NotFoundPage";
import { PrivatePageMetadata } from "@/features/marketing/PrivatePageMetadata";

// Chargées à la demande : le visiteur du site public ne télécharge ni l'app
// (éditeur d'emails, listes, réglages), ni les formulaires d'authentification,
// ni le rendu Markdown des pages légales. Pendant le téléchargement, la page
// affichée reste, sous un voile de chargement (RouteLoadingOverlay).
const LegalPage = lazyPage(() =>
  import("@/pages/marketing/LegalPage").then((m) => ({ default: m.LegalPage })),
);
const LoginPage = lazyPage(() =>
  import("@/features/auth/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazyPage(() =>
  import("@/features/auth/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const ForgotPasswordPage = lazyPage(() =>
  import("@/features/auth/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazyPage(() =>
  import("@/features/auth/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const AuthCallbackPage = lazyPage(() =>
  import("@/features/auth/AuthCallbackPage").then((m) => ({
    default: m.AuthCallbackPage,
  })),
);
const GmailCallbackPage = lazyPage(() =>
  import("@/features/settings/GmailCallbackPage").then((m) => ({
    default: m.GmailCallbackPage,
  })),
);
const OnboardingPage = lazyPage(() =>
  import("@/features/onboarding/OnboardingPage").then((m) => ({
    default: m.OnboardingPage,
  })),
);
const AppShell = lazyPage(() =>
  import("@/components/AppShell").then((m) => ({ default: m.AppShell })),
);
const WorklistPage = lazyPage(() =>
  import("@/pages/WorklistPage").then((m) => ({ default: m.WorklistPage })),
);
const SettingsPage = lazyPage(() =>
  import("@/features/settings/SettingsPage").then((m) => ({
    default: m.SettingsPage,
  })),
);
const AnalyticsPage = lazyPage(() =>
  import("@/features/analytics/AnalyticsPage").then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const LateNoticeRoute = lazyPage(() =>
  import("@/features/late-notice/LateNoticeRoute").then((m) => ({
    default: m.LateNoticeRoute,
  })),
);
const SessionRoute = lazyPage(() =>
  import("@/features/session/SessionRoute").then((m) => ({
    default: m.SessionRoute,
  })),
);

function App() {
  return (
    <>
      <Suspense fallback={<RouteFallback fullScreen />}>
        <PrivatePageMetadata />
        <Routes>
          {/* Pages marketing publiques */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/tarifs" element={<PricingPage />} />
            <Route
              path="/legal/confidentialite"
              element={<LegalPage doc="privacy" />}
            />
            <Route path="/legal/cgu" element={<LegalPage doc="terms" />} />
            <Route
              path="/legal/sous-traitants"
              element={<LegalPage doc="subprocessors" />}
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* Auth (sans layout marketing ni AppShell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route
            path="/auth/gmail/callback"
            element={
              <RequireAuth>
                <GmailCallbackPage />
              </RequireAuth>
            }
          />
          <Route
            path="/onboarding"
            element={
              <RequireAuth>
                <OnboardingPage />
              </RequireAuth>
            }
          />

          {/* Application authentifiée — montée sous /app */}
          <Route
            path="/app"
            element={
              <RequireAuth>
                <RequireOrganization>
                  <AppShell />
                </RequireOrganization>
              </RequireAuth>
            }
          >
            <Route index element={<WorklistPage />} />
            <Route
              path="entities"
              element={<Navigate to="/app?status=all" replace />}
            />
            <Route
              path="groups"
              element={<Navigate to="/app?view=group" replace />}
            />
            <Route path="analyse" element={<AnalyticsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route
              path="entities/:entityId/late-notice"
              element={<LateNoticeRoute />}
            />
            <Route path="session" element={<SessionRoute />} />
          </Route>

          {/* Compat anciens bookmarks : tout /chose → /app/chose */}
          <Route
            path="/settings"
            element={<Navigate to="/app/settings" replace />}
          />
          <Route
            path="/entities"
            element={<Navigate to="/app?status=all" replace />}
          />
          <Route
            path="/groups"
            element={<Navigate to="/app?view=group" replace />}
          />
          <Route
            path="/entities/:entityId/late-notice"
            element={<LegacyLateNoticeRedirect />}
          />
          <Route
            path="/session"
            element={<Navigate to="/app/session" replace />}
          />

        </Routes>
      </Suspense>
      <RouteLoadingOverlay />
    </>
  );
}

function LegacyLateNoticeRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`/app${pathname}${search}`} replace />;
}

export default App;
